import { Component } from '@angular/core';
import { SpinnerComponent } from "../../components/spinner/spinner.component";
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { LucideAngularModule } from 'lucide-angular';
import { EopAssistantComponent } from '../../components/eop-assistant/eop-assistant.component';
import { IntroductionComponent } from '../../components/introduction/introduction.component';
import { Icons } from '../../../shared/helpers/icons';
import { IThreat } from '../../../shared/models/threat';
import { UserDataService } from '../../../shared/services/userData.service';
import { ThreatService } from '../../../shared/services/threat.service';
import { PopupMessageComponent } from '../../components/popup-message/popup-message.component';
import { PrvNextButtonsComponent } from '../../components/prv-next-buttons/prv-next-buttons.component';
import { ThreatObjectivesEditComponent } from './threat-objectives-edit/threat-objectives-edit.component';
import { ThreatObjectivesDetailsComponent } from './threat-objectives-details/threat-objectives-details.component';
import { DeleteConfirmationComponent } from '../../components/delete-confirmation/delete-confirmation.component';
import { ESectionTypes } from '../../../shared/models/eop_types.enum';
import { EopNavigationService } from '../../../shared/services/eopNavigation.service';
import { IUserInfo } from '../../../shared/models/contact';
import { NgxPaginationModule } from 'ngx-pagination';

@Component({
  selector: 'threats-hazards-objectives',
  standalone: true,
  imports: [
    CommonModule,
    LucideAngularModule,
    EopAssistantComponent,
    IntroductionComponent,
    SpinnerComponent,
    PopupMessageComponent,
    PrvNextButtonsComponent,
    DeleteConfirmationComponent,
    ThreatObjectivesEditComponent,
    ThreatObjectivesDetailsComponent,
    NgxPaginationModule
  ],
  templateUrl: './threats-hazards-objectives.component.html',
  styleUrl: './threats-hazards-objectives.component.css'
})
export class ThreatsHazardsObjectivesComponent {
  sectionTypeId = ESectionTypes["Threat & Hazard"];

  loading: boolean = false;
  sortOrder: boolean = true;
  showThreatObjectivesDetail: boolean = false;
  showEditThreatObjectives: boolean = false;
  showCreateNewThreatObjectives: boolean = false;
  showDeleteConfirmation: boolean = false;

  icons = Icons;
  currentBuildingId: string | null = null;
  currentUser: IUserInfo | null = null;
  threatsList: IThreat[] = [];
  messageModal: string | null = null;
  popupMessage: string = '';
  selectedThreat: IThreat | null = null;
  threatDetails: any | null = null;
  threatToEdit: IThreat | null = null;
  page = 1;
  itemsPerPage = 10;

  constructor(
    private userDataService: UserDataService,
    private threatService: ThreatService,
    public eopNavigationService: EopNavigationService
  ) {}

  ngOnInit(): void {
    this.currentBuildingId = this.userDataService.getBuildingId();
    this.currentUser = this.userDataService.getUserData();
    this.loadThreatsGoals();
  }

  loadThreatsGoals(): void {
    if (this.currentBuildingId) {
      this.loading = true;
      this.threatService.getThreatGoals(this.currentBuildingId).subscribe({
        next: (response) => {
          this.threatsList = response.data ? response.data : [];
          this.loading = false;
          console.log(response)
        },
        error: (error: any) => {
          this.loading = false;
          console.error('Error loading threats:', error.message);
        }
      })
    }
  }

  // sort threat-goal added to header of table
  sortList(key: keyof typeof this.threatsList[0]): void {
    this.sortOrder = !this.sortOrder;
    this.threatsList.sort((a, b) => {
      const valueA = a[key]?.toString().toLowerCase() || '';
      const valueB = b[key]?.toString().toLowerCase() || '';

      if (valueA < valueB) {
        return this.sortOrder ? -1 : 1;
      }
      if (valueA > valueB) {
        return this.sortOrder ? 1 : -1;
      }
      return 0;
    });
  }

  handleOpenModal(modalName: string, selectedItem?: any): void {
    if (modalName === 'new-threat-goal') {
      this.showCreateNewThreatObjectives = true;
    } else if (modalName === 'edit-threat-goal') {
      this.showEditThreatObjectives = true;
    } else if (modalName === 'threat-goal-details') {
      this.showThreatObjectivesDetail = true;
    }  else if (modalName === 'delete-confirmation') {
      this.showDeleteConfirmation = true;
    }
    this.selectedThreat = selectedItem ? selectedItem : null;
  }

  handleCloseModal(modalName: string): void {
    if (modalName === 'new-threat-goal') {
      this.showCreateNewThreatObjectives = false;
    } else if (modalName === 'edit-threat-goal') {
      this.showEditThreatObjectives = false;
    } else if (modalName === 'threat-goal-details') {
      this.showThreatObjectivesDetail = false;
    } else if (modalName === 'delete-confirmation') {
      this.showDeleteConfirmation = false;
    }
    this.selectedThreat = null;
  }

  handleSave(message: {type: string, message: string}, actionType: string): void {
    this.handleCloseModal(actionType);
    this.loadThreatsGoals();

    if (message.type === 'success') {
      this.messageModal = 'success';
    } else if (message.type === 'unsuccess') {
      this.messageModal = 'unsuccess';
    } 
    this.popupMessage = message.message;
  }

  handleDelete(item: any): void {
    this.loading = true;
    this.showDeleteConfirmation = false;
    this.threatService.deleteThreatGoals(item.id).subscribe({
      next: (response) => {
        this.loading = false;
        this.messageModal = "success";
        this.popupMessage = 'This occurred after deleting a threat/hazard goals and objectives.';
        this.loadThreatsGoals();
        console.log(response)
      },
      error: (error: any) => {
        this.loading = false;
        this.messageModal = "unsuccess";
        console.error('Error deleting threat goals and objectives:', error.message);
      }
    })
  }
  
  canSave(): boolean {
    if (!this.currentUser) return false;
    return this.currentUser.can_edit || this.currentUser.can_admin;
  }

  resetMessageModal(): void {
    this.messageModal = null;
    this.popupMessage = '';
  }
}
